/**
 * VideoStage — ReactPlayer wrapper + custom controls bar.
 *
 * Wraps `react-player` (already a dep, used in EstadisticasNew.js).
 * Maintains a 16:9 aspect ratio. Controls below the player:
 *   - Play / Pause
 *   - Skip ±5s / ±10s
 *   - Seek slider (videoCurrentTime / videoDuration)
 *   - Speed selector
 *   - Offset adjuster (±1s on the action recording offset)
 *
 * The hook of truth is useVideoPlayer — this component is purely visual.
 */

import React from 'react';
import ReactPlayer from 'react-player';
import { Box, IconButton, Slider, Select, MenuItem, Tooltip, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import Forward5Icon from '@mui/icons-material/Forward5';
import Forward10Icon from '@mui/icons-material/Forward10';
import Replay5Icon from '@mui/icons-material/Replay5';
import Replay10Icon from '@mui/icons-material/Replay10';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { useStatsTheme, withAlpha } from '../theme/statsTheme';
import { formatTime } from '../../utils/timeUtils';

const PLAYBACK_RATES = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

export default function VideoStage({
  playerRef,
  videoUrl,
  videoPlaying,
  videoDuration,
  videoCurrentTime,
  playbackRate,
  videoOffset,
  onProgress,
  onDuration,
  onTogglePlay,
  onSeek,
  onSkip,
  onPlaybackRateChange,
  onOffsetChange,
  height = 'auto',
}) {
  const theme = useStatsTheme();

  const handleSliderChange = (_, value) => {
    if (typeof onSeek === 'function') onSeek(value);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.surfaces.base,
        borderRadius: `${theme.radius.lg}px`,
        overflow: 'hidden',
        boxShadow: theme.shadows.lg,
        border: `1px solid ${theme.surfaces.border}`,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          paddingTop: height === 'auto' ? '56.25%' : 0, // 16:9
          height: height === 'auto' ? 0 : height,
          backgroundColor: '#000',
        }}
      >
        {videoUrl ? (
          <ReactPlayer
            ref={playerRef}
            url={videoUrl}
            playing={videoPlaying}
            onProgress={onProgress}
            onDuration={onDuration}
            playbackRate={playbackRate}
            controls={false}
            width="100%"
            height="100%"
            style={{ position: 'absolute', top: 0, left: 0 }}
            config={{ youtube: { playerVars: { modestbranding: 1, rel: 0 } } }}
          />
        ) : (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.text.muted,
              fontSize: 14,
            }}
          >
            No video
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          backgroundColor: theme.surfaces.elevated,
          borderTop: `1px solid ${theme.surfaces.divider}`,
        }}
      >
        <Tooltip title="-10s">
          <IconButton size="small" onClick={() => onSkip && onSkip(-10)} sx={{ color: theme.text.primary }}>
            <Replay10Icon sx={{ fontSize: 22 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="-5s">
          <IconButton size="small" onClick={() => onSkip && onSkip(-5)} sx={{ color: theme.text.primary }}>
            <Replay5Icon sx={{ fontSize: 22 }} />
          </IconButton>
        </Tooltip>

        <Tooltip title={videoPlaying ? 'Pause' : 'Play'}>
          <IconButton
            onClick={onTogglePlay}
            sx={{
              width: 44,
              height: 44,
              backgroundColor: theme.accentSoft,
              border: `1px solid ${theme.accentBorder}`,
              color: theme.accent,
              '&:hover': { backgroundColor: withAlpha(theme.accent, 0.24) },
            }}
            aria-label={videoPlaying ? 'pause' : 'play'}
          >
            {videoPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
        </Tooltip>

        <Tooltip title="+5s">
          <IconButton size="small" onClick={() => onSkip && onSkip(5)} sx={{ color: theme.text.primary }}>
            <Forward5Icon sx={{ fontSize: 22 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="+10s">
          <IconButton size="small" onClick={() => onSkip && onSkip(10)} sx={{ color: theme.text.primary }}>
            <Forward10Icon sx={{ fontSize: 22 }} />
          </IconButton>
        </Tooltip>

        <Box sx={{ flex: 1, mx: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Slider
            min={0}
            max={Math.max(1, videoDuration || 0)}
            value={Math.min(videoCurrentTime || 0, videoDuration || 0)}
            onChange={handleSliderChange}
            sx={{
              color: theme.accent,
              height: 4,
              '& .MuiSlider-thumb': { width: 14, height: 14 },
              '& .MuiSlider-rail': { backgroundColor: withAlpha(theme.text.muted, 0.4) },
            }}
            aria-label="seek video"
          />
          <Typography
            sx={{
              fontFamily: theme.typography.fontFamily,
              fontSize: 11,
              fontWeight: 600,
              color: theme.text.secondary,
              fontVariantNumeric: 'tabular-nums',
              minWidth: 90,
              textAlign: 'right',
            }}
          >
            {formatTime(videoCurrentTime || 0)} / {formatTime(videoDuration || 0)}
          </Typography>
        </Box>

        <Select
          size="small"
          value={playbackRate}
          onChange={(e) => onPlaybackRateChange && onPlaybackRateChange(Number(e.target.value))}
          aria-label="playback rate"
          sx={{
            color: theme.text.primary,
            fontSize: 12,
            fontWeight: 600,
            height: 32,
            minWidth: 70,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.surfaces.border },
            '& .MuiSvgIcon-root': { color: theme.text.secondary },
          }}
        >
          {PLAYBACK_RATES.map((rate) => (
            <MenuItem key={rate} value={rate}>
              {rate}x
            </MenuItem>
          ))}
        </Select>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.4,
            ml: 1,
            px: 0.8,
            py: 0.2,
            borderRadius: theme.radius.pill,
            backgroundColor: withAlpha(theme.actionColors.warning, 0.14),
            border: `1px solid ${withAlpha(theme.actionColors.warning, 0.34)}`,
          }}
        >
          <Tooltip title="Offset -1s">
            <IconButton
              size="small"
              onClick={() => onOffsetChange && onOffsetChange((videoOffset || 0) - 1)}
              sx={{ width: 22, height: 22, color: theme.actionColors.warning }}
            >
              <RemoveIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: theme.actionColors.warning, minWidth: 32, textAlign: 'center' }}>
            {videoOffset >= 0 ? `+${videoOffset}s` : `${videoOffset}s`}
          </Typography>
          <Tooltip title="Offset +1s">
            <IconButton
              size="small"
              onClick={() => onOffsetChange && onOffsetChange((videoOffset || 0) + 1)}
              sx={{ width: 22, height: 22, color: theme.actionColors.warning }}
            >
              <AddIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}
